<template>
  <div :class="classObj" class="app-wrapper">
    <div
      v-if="classObj.mobile && sidebar.opened"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <slot name="sidebar" />
    <div :class="classObj.cls" class="main-container">
      <Sticky :z-index="10">
        <Navbar>
          <template #right-menu>
            <slot name="right-menu" />
          </template>
        </Navbar>
      </Sticky>
      <AppMain>
        <template #notification>
          <slot name="notification" />
        </template>
      </AppMain>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useAppStore } from '@/store/modules/app'
import { AppMain, Navbar } from './components'
import useResize from '@/hooks/useResize'
import Sticky from '@/components/Sticky.vue'
import useRouter from '@/hooks/useRouter'

// Define component
export default defineComponent({
  name: 'AppLayout',
  components: {
    AppMain,
    Navbar,
    Sticky
  },
  setup() {
    const { device, sidebar } = useResize() // Assuming useResize hook is working correctly

    // Tapping the backdrop closes the drawer. The store exposes `CloseSideBar` — this read
    // `appStore.closeSideBar`, which is neither that nor the private `closeSidebar` behind
    // it, so the tap threw instead of closing anything. `false` keeps the animation; only
    // the resize handler closes it without one.
    const handleClickOutside = () => {
      const appStore = useAppStore()
      appStore.CloseSideBar(false)
    }

    const { route } = useRouter() // Assuming this is a custom router hook

    // Computed property for dynamic class binding
    const classObj = computed(() => {
      return {
        hideSidebar: !sidebar.value.opened,
        openSidebar: sidebar.value.opened,
        withoutAnimation: sidebar.value.withoutAnimation,
        mobile: device.value === 'mobile', // Assuming 'mobile' is the device type
        cls: route.value.name
      }
    })

    return { handleClickOutside, device, sidebar, classObj }
  }
})
</script>

<style lang="scss">
@use '@/styles/_mixins.scss' as *;
.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.main-container {
  min-height: 100%;
  transition: margin-left 0.28s;
  margin-left: var(--side-bar-width);
  position: relative;
}

.sidebar-container {
  transition: width 0.28s;
  width: var(--side-bar-width) !important;
  height: 100%;
  position: fixed;
  font-size: 0px;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  overflow: hidden;
}

.hideSidebar {
  .main-container {
    margin-left: 54px;
  }

  .sidebar-container {
    width: 54px !important;
  }
}

.openSidebar {
  :deep(.app-footer) {
    padding-right: 210px;
  }
}

/* for mobile response 适配移动端 */
.mobile {
  .main-container {
    margin-left: 0px;
  }

  .sidebar-container {
    transition: transform 0.28s;
    width: var(--side-bar-width) !important;
  }

  &.openSidebar {
    position: fixed;
    top: 0;
  }

  &.hideSidebar {
    .sidebar-container {
      pointer-events: none;
      transition-duration: 0.3s;
      transform: translate3d(-var(--side-bar-width), 0, 0);
    }
  }
}

.withoutAnimation {
  .main-container,
  .sidebar-container {
    transition: none;
  }
}
</style>
